import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

type UseMicDictationOpts = {
  onText: (fullText: string) => void
  getBaseText: () => string
}

/**
 * Chrome Web Speech often ends the session after a short pause.
 * We keep a "wantListening" flag and auto-restart until the user stops.
 */
export const useMicDictation = ({ onText, getBaseText }: UseMicDictationOpts) => {
  const [isRecording, setIsRecording] = useState(false)
  const wantListeningRef = useRef(false)
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const baseRef = useRef('')
  const finalsRef = useRef('')
  const startingRef = useRef(false)
  const onTextRef = useRef(onText)
  const getBaseTextRef = useRef(getBaseText)

  useEffect(() => {
    onTextRef.current = onText
    getBaseTextRef.current = getBaseText
  }, [onText, getBaseText])

  const pushText = useCallback((finals: string, interim: string) => {
    onTextRef.current(baseRef.current + finals + interim)
  }, [])

  const stop = useCallback(() => {
    wantListeningRef.current = false
    startingRef.current = false
    setIsRecording(false)
    const rec = recognitionRef.current
    recognitionRef.current = null
    if (!rec) return
    try {
      rec.onresult = null
      rec.onerror = null
      rec.onend = null
      rec.onstart = null
      rec.stop()
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => () => stop(), [stop])

  const beginRecognition = useCallback(() => {
    if (!wantListeningRef.current || startingRef.current) return

    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!Ctor) {
      toast.error('Voice input needs Chrome.')
      wantListeningRef.current = false
      setIsRecording(false)
      return
    }

    startingRef.current = true

    // Tear down previous instance cleanly
    try {
      const prev = recognitionRef.current
      recognitionRef.current = null
      if (prev) {
        prev.onend = null
        prev.onerror = null
        prev.onresult = null
        prev.stop()
      }
    } catch {
      /* ignore */
    }

    const recognition = new Ctor()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = 'en-US'
    recognition.maxAlternatives = 1
    recognitionRef.current = recognition

    recognition.onstart = () => {
      startingRef.current = false
      if (wantListeningRef.current) setIsRecording(true)
    }

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      if (!wantListeningRef.current) return

      let interim = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const piece = event.results[i][0]?.transcript ?? ''
        if (event.results[i].isFinal) {
          finalsRef.current += piece
        } else {
          interim += piece
        }
      }
      pushText(finalsRef.current, interim)
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      startingRef.current = false
      const err = event.error
      // These are normal during continuous listening — we'll restart on onend
      if (err === 'aborted' || err === 'no-speech') return

      wantListeningRef.current = false
      setIsRecording(false)
      recognitionRef.current = null

      if (err === 'not-allowed' || err === 'service-not-allowed') {
        toast.error('Allow microphone access in the browser address bar.')
      } else if (err === 'network') {
        toast.error('Speech needs internet — Chrome sends audio to Google for transcription.')
      } else {
        toast.error(`Mic error: ${err}`)
      }
    }

    recognition.onend = () => {
      startingRef.current = false
      // Commit whatever finals we have so far
      pushText(finalsRef.current, '')

      if (!wantListeningRef.current) {
        setIsRecording(false)
        recognitionRef.current = null
        return
      }

      // Chrome ended the session mid-listen — restart to keep capturing
      window.setTimeout(() => {
        if (!wantListeningRef.current) return
        beginRecognition()
      }, 80)
    }

    try {
      recognition.start()
    } catch {
      startingRef.current = false
      // Retry once shortly (common right after a previous stop)
      window.setTimeout(() => {
        if (!wantListeningRef.current) return
        try {
          recognition.start()
        } catch (e) {
          console.error(e)
          wantListeningRef.current = false
          setIsRecording(false)
          toast.error('Could not start microphone.')
        }
      }, 150)
    }
  }, [pushText])

  const start = useCallback(async () => {
    if (typeof window === 'undefined') return

    if (!window.isSecureContext) {
      toast.error('Open http://localhost:5173 (mic blocked on IP addresses).')
      return
    }

    const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!Ctor) {
      toast.error('Voice input needs Chrome.')
      return
    }

    // Unlock mic permission first — makes capture much more reliable
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach((t) => t.stop())
      }
    } catch {
      toast.error('Microphone permission denied. Allow mic and try again.')
      return
    }

    baseRef.current = getBaseTextRef.current()
    finalsRef.current = ''
    wantListeningRef.current = true
    setIsRecording(true)
    beginRecognition()
  }, [beginRecognition])

  const toggle = useCallback(() => {
    if (wantListeningRef.current || isRecording) stop()
    else void start()
  }, [isRecording, start, stop])

  return { isRecording, toggle, stop }
}
